import { useCallback, useEffect, useRef, useState } from 'react';

interface PageInfo {
	currentPage: number;
	totalPages: number;
	totalCount: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

interface UseInfiniteScrollOptions<TItem> {
	/** The `data` array from the current page response */
	items: TItem[] | undefined | null;
	/** The `pageInfo` from the current page response */
	pageInfo: PageInfo | undefined | null;
	/** Whether the query is currently loading */
	loading: boolean;
	/** Value that, when changed, resets accumulated items and page (e.g. debounced search) */
	resetKey?: string;
}

interface UseInfiniteScrollResult<TItem> {
	/** All accumulated items across pages */
	items: TItem[];
	/** Current page number — pass this to your query's pagination input */
	page: number;
	/** Whether more pages are being loaded (not the initial load) */
	loadingMore: boolean;
	/** Whether this is the initial load (page 1, no items yet) */
	initialLoading: boolean;
	/** Whether there are more pages to load */
	hasMore: boolean;
	/** Ref callback to attach to a sentinel element at the bottom of the list */
	sentinelRef: (node: HTMLElement | null) => void;
	/** Reset back to page 1 and clear accumulated items */
	reset: () => void;
}

/**
 * Hook for infinite-scroll pagination over any Apollo paginated query.
 *
 * Usage:
 * ```ts
 * const scroll = useInfiniteScroll({ items: data?.getAll?.data, pageInfo: data?.getAll?.pageInfo, loading, resetKey: search });
 * // pass scroll.page into your query variables: { pagination: { currentPage: scroll.page, limit } }
 * // render scroll.items, place <div ref={scroll.sentinelRef} /> at the end of the list
 * ```
 */
export function useInfiniteScroll<TItem>({
	items: pageItems,
	pageInfo,
	loading,
	resetKey = '',
}: UseInfiniteScrollOptions<TItem>): UseInfiniteScrollResult<TItem> {
	const [page, setPage] = useState(1);
	const [accumulated, setAccumulated] = useState<TItem[]>([]);
	const prevResetKey = useRef(resetKey);
	const prevPage = useRef(page);

	// Reset when resetKey changes (e.g. search query)
	if (prevResetKey.current !== resetKey) {
		prevResetKey.current = resetKey;
		setPage(1);
		setAccumulated([]);
	}

	// Append new page data when it arrives
	useEffect(() => {
		if (!pageItems || pageItems.length === 0) return;

		if (page === 1) {
			setAccumulated(pageItems);
		} else if (page !== prevPage.current || accumulated.length === 0) {
			setAccumulated((prev) => {
				const existingIds = new Set(prev.map((item) => (item as { id: string }).id));
				const newItems = pageItems.filter(
					(item) => !existingIds.has((item as { id: string }).id)
				);
				if (newItems.length === 0) return prev;
				return [...prev, ...newItems];
			});
		}
		prevPage.current = page;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageItems, page]);

	const hasMore = pageInfo?.hasNextPage ?? false;

	// Intersection observer for infinite scroll
	const observer = useRef<IntersectionObserver | null>(null);
	const sentinelRef = useCallback(
		(node: HTMLElement | null) => {
			if (observer.current) observer.current.disconnect();

			if (!node || loading || !hasMore) return;

			observer.current = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						setPage((p) => p + 1);
					}
				},
				{ threshold: 0 }
			);

			observer.current.observe(node);
		},
		[loading, hasMore]
	);

	const reset = useCallback(() => {
		setPage(1);
		setAccumulated([]);
		prevPage.current = 1;
	}, []);

	return {
		items: accumulated,
		page,
		loadingMore: loading && page > 1,
		initialLoading: loading && page === 1 && accumulated.length === 0,
		hasMore,
		sentinelRef,
		reset,
	};
}
